import { useQuery } from '@tanstack/react-query';
import { SourcingService, SourcingStageType } from 'api-clients/monolith';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowRightIcon from '@/assets/icons/arrow-right.svg';
import InformationIcon from '@/assets/icons/information.svg';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

export type Inputs = {
  [key in SourcingStageType]: {
    hireStageId: string;
    expectedConversion: number | null;
  };
};

interface FormInputsProps {
  stageType: SourcingStageType;
  stageName: string;
  hookForm: UseFormReturn<Inputs>;
}

export function FormInputs({
  stageType,
  stageName,
  hookForm,
}: FormInputsProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();

  const {
    control,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors, defaultValues },
  } = hookForm;

  const { data, isLoading: isFetchingHireStages } = useQuery({
    queryKey: ['hireStages', openingSlug],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHireStages(openingSlug),
  });

  const hireStagesData = useMemo(() => data?.hire_stages ?? [], [data]);

  // Convert the hire stages data to an object of hire stage id to conversion rate
  const hireStageConversionRates = useMemo(() => {
    return hireStagesData.reduce(
      (acc, hireStage) => ({
        ...acc,
        [hireStage.id]: hireStage.conversion_rate,
      }),
      {} as Record<string, number>,
    );
  }, [hireStagesData]);

  const selectedHireStageId = watch(`${stageType}.hireStageId`);
  const conversion = watch(`${stageType}.expectedConversion`);
  const formValues = getValues();

  // Set the conversion rate to null if the hire stage is not selected and prefill the conversion rate if it is already set
  useEffect(() => {
    if (selectedHireStageId === null) {
      setValue(`${stageType}.expectedConversion`, null, {
        shouldDirty: true,
      });
    } else {
      setValue(
        `${stageType}.expectedConversion`,
        defaultValues?.[stageType]?.expectedConversion ??
          (hireStageConversionRates[selectedHireStageId] || null),
      );
    }
  }, [
    stageType,
    setValue,
    selectedHireStageId,
    defaultValues,
    hireStageConversionRates,
  ]);

  // Get all the hire stage ids that are already mapped to a source stage
  const allSelectedHireStageIds = useMemo(
    () => Object.values(formValues).map(value => value.hireStageId),
    [formValues],
  );

  const hireStageOptions = useMemo(
    () =>
      hireStagesData
        // If the hire stage is already mapped to a source stage, do not show it in the options
        .filter(hireStage => {
          return (
            !allSelectedHireStageIds.includes(hireStage.id) ||
            hireStage.id === selectedHireStageId
          );
        })
        .map(hireStage => ({
          label: hireStage.title,
          value: hireStage.id,
        })) ?? [],
    [hireStagesData, allSelectedHireStageIds, selectedHireStageId],
  );

  const selectedHireStageConversion = useMemo(
    () => hireStageConversionRates[selectedHireStageId] || null,
    [hireStageConversionRates, selectedHireStageId],
  );

  // Reset the conversion rate to the selected hire stage conversion rate
  const handleResetConversion = useCallback(() => {
    setValue(`${stageType}.expectedConversion`, selectedHireStageConversion, {
      shouldDirty: true,
    });
  }, [setValue, stageType, selectedHireStageConversion]);

  return (
    <div
      className={clsx(
        'bg-fx-neutral-50 grid grid-cols-[1fr_24px_2fr_1.3fr] items-center gap-6 rounded p-4',
        !!selectedHireStageConversion && 'pb-7',
      )}
    >
      <Typography variant="h6">{stageName}</Typography>
      <img src={ArrowRightIcon} alt="" aria-hidden="true" />

      <FormSelect
        control={control}
        name={`${stageType}.hireStageId`}
        options={hireStageOptions}
        isLoading={isFetchingHireStages}
        isMultiSelect={false}
        className="rounded-sm"
        placeholder={intl.formatMessage({
          defaultMessage: 'Select workflow stage',
        })}
        isClearable
      />

      {stageType !== 'hired' && (
        <div className="relative">
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1">
              <Input
                isNumber
                placeholderLabel="%"
                {...register(`${stageType}.expectedConversion`, {
                  setValueAs: value => (value === '' ? null : Number(value)),
                })}
                min={0}
                max={100}
                error={errors[stageType]?.expectedConversion?.message}
                onReset={
                  !!selectedHireStageConversion &&
                  conversion !== selectedHireStageConversion
                    ? handleResetConversion
                    : undefined
                }
                resetTooltipContent={intl.formatMessage({
                  defaultMessage:
                    'Reset conversion rate to system estimate based on past data',
                })}
                disabled={!selectedHireStageId}
              />
            </div>
            {stageType === 'landing' && (
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'This value is same as target conversion rate for this opening.',
                })}
              >
                <span className="flex cursor-default items-center">
                  <img src={InformationIcon} alt="Status" />
                </span>
              </Tooltip>
            )}
          </div>

          {!!selectedHireStageConversion && (
            <Typography
              variant="small1"
              className="!text-fx-neutral-600 absolute top-10"
            >
              <FormattedMessage
                defaultMessage="Conversion estimate: {conversion}%"
                values={{ conversion: selectedHireStageConversion }}
              />
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}
